import { useParams, useNavigate } from "react-router-dom";
import { Typography, Box, Button, Card, CardContent, Alert, Chip, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CodeIcon from "@mui/icons-material/Code";

interface ComponentInfo {
  id: string;
  name: string;
  description: string;
  category: string;
  features: string[];
  example: string;
}

const COMPONENTS: Record<string, ComponentInfo> = {
  "button-showcase": {
    id: "button-showcase",
    name: "Button Showcase",
    description: "Collection of button styles matching Insight's design system.",
    category: "Interactive",
    features: [
      "Primary, secondary, outlined variants",
      "Consistent with Insight theme",
      "No text transform (textTransform: none)",
      "8px border radius",
    ],
    example: `<Button variant="contained" color="primary">
  Primary Action
</Button>`,
  },
  "card-layouts": {
    id: "card-layouts",
    name: "Card Layouts",
    description: "Card components with elevation 0 and rounded corners.",
    category: "Layout",
    features: [
      "Flat design (elevation 0)",
      "8px border radius",
      "Responsive grid layout",
      "Interactive elements",
    ],
    example: `<Card>
  <CardContent>
    <Typography variant="h6">Card Title</Typography>
    <Typography variant="body2" color="text.secondary">
      Card content here
    </Typography>
  </CardContent>
</Card>`,
  },
  "color-palette": {
    id: "color-palette",
    name: "Color Palette",
    description: "Insight project color system reference.",
    category: "Design System",
    features: [
      "Primary blue (40, 109, 178)",
      "Secondary green (120, 168, 66)",
      "Brand navy (5, 36, 65)",
      "Accent colors: pink, orange, yellow",
    ],
    example: `<Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2 }}>
  Primary Color
</Box>`,
  },
};

/**
 * Component detail page showing information about a specific component.
 *
 * Demonstrates internal routing within a component section and
 * breadcrumb navigation back to the main page.
 */
export function ComponentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const component = id ? COMPONENTS[id] : undefined;

  if (!component) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          Component not found: {id}
        </Alert>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate("/")}>
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          size="small"
        >
          Back to Components
        </Button>
      </Box>

      {/* Component Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "start", gap: 2, mb: 2 }}>
            <CodeIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {component.name}
              </Typography>
              <Chip label={component.category} color="primary" size="small" />
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {component.description}
          </Typography>
        </CardContent>
      </Card>

      {/* Features */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Features
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
            {component.features.map((feature, index) => (
              <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                {feature}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Code Example */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Example Code
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            component="pre"
            sx={{
              bgcolor: "background.default",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              fontFamily: "monospace",
              fontSize: "14px",
            }}
          >
            <code>{component.example}</code>
          </Box>
        </CardContent>
      </Card>

      {/* Usage Notes */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Integration Note:</strong> This component uses Insight's theme and can be copied
          directly into the twosion-ui project. The theme provider will handle all styling
          automatically.
        </Typography>
      </Alert>
    </Box>
  );
}
